import type { ProspectDefaultTag } from './api';

export const SCHOLARSHIP_MODELS = ['FullHalf', 'Percentage', 'Equivalency'] as const;
export type ScholarshipModel = typeof SCHOLARSHIP_MODELS[number];

export const SCHOLARSHIP_UNITS = [1.0, 0.5] as const;

// A configured per-position budget (raw unit + dollar values; active model picks one).
export interface PositionBudget {
  position: ScholarshipPlayerPosition;
  budgetUnits: number | null;
  budgetDollars: number | null;
  // How many players the coach wants here. null = no target stated, which the needs card
  // shows as a dash — 0 would mean "none wanted", a different plan.
  playersNeeded: number | null;
  note: string | null;
}

// Computed per-position budget usage (active model) for one position.
export interface PositionBudgetSummary {
  position: ScholarshipPlayerPosition;
  budget: number | null;
  used: number;
  remaining: number | null;
  percentUsed: number | null;
  isOver: boolean;
  playersNeeded: number | null;
  // Counted by the roster row's position, not the player record's — the board owns where a
  // player sits.
  committedCount: number;
  offeredCount: number;
}

export interface ScholarshipSummary {
  rosterFilled: number;
  rosterRemaining: number | null;
  isOverRosterLimit: boolean;
  allocationUsed: number;
  allocationAvailable: number | null;
  allocationRemaining: number | null;
  allocationPercentUsed: number | null;
  isOverBudget: boolean;
  // Average scholarship per funded player (model unit). averageValue is null when no
  // players are funded. isOverAverageTarget is true only when current > target (strict).
  averageValue: number | null;
  averageTarget: number | null;
  isOverAverageTarget: boolean;
  // Per-position budget usage (active model), one entry per configured position budget.
  positionBudgets: PositionBudgetSummary[];
  // Model-independent NIL / Rev Share buckets — always computed server-side;
  // the UI hides them when the plan's tracking toggles are off.
  nilUsed: number;
  nilRemaining: number | null;
  nilPercentUsed: number | null;
  isOverNilBudget: boolean;
  // Recruiting progress. Zero-filled rather than nullable: a board with no needs and nobody
  // committed is genuinely at 0 of 0. committedPercentOfTarget is null when no target is set,
  // so the card can say "no target" instead of implying no progress.
  targetCount: number;
  committedCount: number;
  offeredCount: number;
  committedPercentOfTarget: number | null;
  // Player counts by class, whichever shape the board holds — a graduation year or a year of
  // eligibility. A row with no class at all is not a bucket, so a board of blanks reports an
  // empty list.
  classBreakdown: ClassCount[];
  revShareUsed: number;
  revShareRemaining: number | null;
  revSharePercentUsed: number | null;
  isOverRevShareBudget: boolean;
  /**
   * Plan-wide sum of academicAmount over non-deleted players, zero-filled. A plain roster
   * total and nothing more: it has no budget, no tracking toggle, and no remaining/percent/
   * over-budget companions, because academic money is not measured against an athletic cap.
   * It feeds none of the athletic maths above.
   *
   * Optional in the TYPE only, to model deploy skew — the API always sends it. Web and the API
   * ship as separate jobs off the same push, so a web build can briefly be talking to an API
   * that predates this field. Declaring it non-optional would make the `?? 0` at the one read
   * site look like dead code, and the next person would delete it and print "$NaN" on the
   * board for the length of a deploy.
   */
  academicTotal?: number;
}

// What a roster is for. Chosen at creation, where it seeds the summary band; a label from
// then on. Mirrors ScholarshipPurposes on the API — change both together.
export const SCHOLARSHIP_PURPOSES = ['Recruiting', 'Scholarship'] as const;
export type ScholarshipPurpose = typeof SCHOLARSHIP_PURPOSES[number];

export interface ClassCount {
  /**
   * The class as the board holds it: a graduation year ("2027") or a year of eligibility
   * ("3"). Optional because web and API deploy as separate jobs, so a newer web build can be
   * talking to an API that only sends gradYear — read it through classCountLabel rather than
   * directly.
   */
  classLabel?: string;
  /** Set when the bucket is a graduation year; null for a bucket of eligibility years. */
  gradYear: number | null;
  count: number;
}

export interface Scholarship {
  id: string;
  name: string;
  purpose: ScholarshipPurpose;
  isArchived: boolean;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  scholarshipModel: ScholarshipModel;
  budgetUnits: number | null;
  budgetDollars: number | null;
  rosterLimit: number | null;
  targetAverageUnits: number | null;
  targetAverageDollars: number | null;
  nilTrackingEnabled: boolean;
  nilBudget: number | null;
  revShareTrackingEnabled: boolean;
  revShareBudget: number | null;
  // Ordered summary card keys. Already resolved by the API: a plan that has never been
  // configured arrives with the default set rather than null, so the client never has to
  // know that "never configured" and "show the defaults" are separate states server-side.
  // An empty array is meaningful and means "show no cards".
  //
  // Deliberately string[] rather than SummaryCardKey[]: a key from a newer build must
  // survive a round trip through an older tab rather than being dropped as unassignable.
  // See summaryCards.ts for the known set.
  visibleCards: string[];
  positionBudgets: PositionBudget[];
  summary: ScholarshipSummary;
  /**
   * The board's option lists, always the EFFECTIVE value: the board's own, or the API's defaults
   * when it has never been configured. The API never sends null.
   *
   * Optional in the TYPE only, to model deploy skew (see academicTotal): web and API ship as
   * separate jobs. Read it through rosterSettingsOf, which falls back to empty lists.
   */
  rosterSettings?: RosterSettings;
  /** True once the board has saved its own settings; false while it is running on the defaults. */
  rosterSettingsCustomized?: boolean;
}

// ----- Roster settings (PUT/DELETE /api/scholarships/{id}/settings) -----

// Colours are palette KEYS, not hex. Each client maps a key to its own token (web: the
// --color-palette-* tokens in index.css), so a colour can be retuned without touching stored data.
// Mirrors RosterPaletteColors on the API.
export const ROSTER_PALETTE_KEYS = [
  'red', 'orange', 'yellow', 'green', 'teal', 'blue', 'purple', 'pink', 'gray', 'black',
] as const;
export type RosterPaletteKey = typeof ROSTER_PALETTE_KEYS[number];

/** Referenced from player rows by `id`, so renaming a trait keeps every player on it. */
export interface RosterEvaluationTrait {
  id: string;
  name: string;
}

export interface RosterIndicator {
  id: string;
  name: string;
  /**
   * A palette key. Typed as string rather than RosterPaletteKey so a key a newer API adds survives
   * a round trip through this build; read it through paletteColorVar, which falls back.
   */
  color: string;
}

export interface RosterHandednessColors {
  left: string;
  right: string;
  switch: string;
}

/**
 * A board's configurable option lists. Arm angle, pitch shape, pitching role, player type and
 * player status are stored on a player row as the label itself, so removing an option leaves the
 * old label displayed as-is. Pitch shapes are short codes (FF, SK, SL...); see pitchShapeLabel.
 */
export interface RosterSettings {
  evaluationTraits: RosterEvaluationTrait[];
  indicators: RosterIndicator[];
  handednessColors: RosterHandednessColors;
  armAngles: string[];
  pitchShapes: string[];
  pitchingRoles: string[];
  playerTypes: string[];
  playerStatuses: string[];
}

/** Body for PUT /api/scholarships/{id}/settings — a full replace, same shape as the response. */
export type RosterSettingsRequest = RosterSettings;

// ----- Lineups (GET /api/scholarships/{id}/lineups, PUT .../lineups/{lhp|rhp}) -----

export type LineupVersus = 'lhp' | 'rhp';

export interface LineupSlot {
  battingOrder: number;
  /** The board row id (ScholarshipPlayer.id), not a prospect id. */
  playerId: string;
  /** Null means the player's primary position. */
  fieldPosition: ScholarshipPlayerPosition | null;
}

export interface ScholarshipLineups {
  vsLhp: LineupSlot[];
  vsRhp: LineupSlot[];
}

/** Body for PUT /api/scholarships/{id}/lineups/{versus}: a full replace of one side. */
export interface ScholarshipLineupRequest {
  slots: LineupSlot[];
}

// Body for PUT /api/scholarships/{id}/cards. An empty list is valid ("show no cards") and
// is not read as a reset — the API rejects unknown and repeated keys.
export interface ScholarshipVisibleCardsRequest {
  visibleCards: string[];
}

export interface ScholarshipConfigurationRequest {
  scholarshipModel: ScholarshipModel;
  budgetUnits: number | null;
  budgetDollars: number | null;
  rosterLimit: number | null;
  targetAverageUnits: number | null;
  targetAverageDollars: number | null;
  nilTrackingEnabled: boolean;
  nilBudget: number | null;
  revShareTrackingEnabled: boolean;
  revShareBudget: number | null;
  positionBudgets: PositionBudget[];
}

export interface ScholarshipListItem {
  id: string;
  name: string;
  purpose: ScholarshipPurpose;
  playerCount: number;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  /**
   * Where a coach put this board. The list comes back in `sortOrder` then `updatedAt DESC`, so
   * the order the API sends IS the order to render -- nothing here re-sorts by this field.
   *
   * Optional because web and the API deploy as separate jobs: an older API sends none, and the
   * list is then ordered by `updatedAt` exactly as it always was.
   */
  sortOrder?: number;
}

/** Body for PATCH /api/scholarships/reorder: every non-archived board, in the coach's order. */
export interface ScholarshipReorderRequest {
  orderedIds: string[];
}

/** Body for POST /api/scholarships/{id}/players/{playerId}/transfer. */
export interface ScholarshipTransferPlayerRequest {
  targetScholarshipId: string;
}

export interface ScholarshipNameRequest {
  name: string;
  // Honoured on create only — a rename ignores it, because changing purpose would mean
  // reseeding the card set over whatever the coach had chosen.
  purpose?: ScholarshipPurpose;
}

// Body for POST /api/scholarships/{id}/duplicate. advanceClass moves every cloned
// player one step up SCHOLARSHIP_CLASSES; the source roster is never modified.
export interface ScholarshipDuplicateRequest {
  advanceClass: boolean;
}

// Keep in sync with ScholarshipPlayerPositions on the API, which documents all ten places this
// closed set is duplicated. 'DH' is stored regardless of sport -- softball calls the slot a DP
// and the UI renders it that way for softball programs, but only one value is ever persisted.
// Read a position for display through positionLabel(), never raw.
export const SCHOLARSHIP_PLAYER_POSITIONS = [
  '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'RHP', 'LHP', 'C', 'DH', 'UTIL',
] as const;
export type ScholarshipPlayerPosition = typeof SCHOLARSHIP_PLAYER_POSITIONS[number];

// Years of eligibility, not academic class names — coaches count 1-5. Rows saved under the
// retired FR/Soph/JR/SR/5th/Grad ladder were rewritten to numbers by the
// BackfillScholarshipClassNumbers migration. A value outside the list is still rendered
// verbatim rather than dropped, since nothing here can guarantee what a stray row holds.
// Keep in sync with ScholarshipPlayerClasses on the API.
export const SCHOLARSHIP_CLASSES = ['1', '2', '3', '4', '5'] as const;
export type ScholarshipEligibilityYear = typeof SCHOLARSHIP_CLASSES[number];

// A recruiting board counts in graduation years instead, so a class may also be a four-digit
// year. Both shapes share the one field: which one a value is, is readable from its shape,
// and a board offers one set or the other rather than mixing them. See
// features/dashboard/sections/scholarships/scholarshipClasses.ts for the guards and the list
// of years offered.
export type ScholarshipGraduationYear = `20${number}`;

// A junior college player's year in the program. Shown as "JC 2" -- read one for display
// through classDisplayLabel, never raw. Selectable in the class editor and accepted on a write.
//
// Where it LANDS depends on the row. On a linked row the API routes it to the player record
// (PlayerLevel JUCO plus a JC year), which is where ScholarshipClassResolver reads it back from,
// so the row's own class column stays null; on a manual row it is stored on the row. Either way
// the value a coach picks is the value the board reports back. Mirrors ApplyClassWrite.
export const SCHOLARSHIP_JUCO_CLASSES = ['JC1', 'JC2', 'JC3'] as const;
export type ScholarshipJucoClass = typeof SCHOLARSHIP_JUCO_CLASSES[number];

// The three shapes a class can actually be stored as. Anything written to the API is one of these.
export type ScholarshipStoredClass =
  | ScholarshipEligibilityYear
  | ScholarshipGraduationYear
  | ScholarshipJucoClass;

// Every class a player can be filed under on the board. The same set today -- kept as its own
// name because it is what display, filtering and ordering are about, and a class that is shown
// but not storable has existed here before.
export type ScholarshipClass = ScholarshipStoredClass;

export const RESIDENCY_VALUES = ['Resident', 'NonResident'] as const;
export type Residency = typeof RESIDENCY_VALUES[number];

export const CAMPUS_VALUES = ['On', 'Off'] as const;
export type Campus = typeof CAMPUS_VALUES[number];

// Where a player stands in recruiting. Lives on the player record, not the roster row, so a
// player cannot be committed on one board and merely offered on another. Mirrors
// RecruitingStatuses on the API — change both together.
export const RECRUITING_STATUSES = ['None', 'Offered', 'Committed'] as const;
export type RecruitingStatus = typeof RECRUITING_STATUSES[number];

/** A team the linked player belongs to. Named by the board; edited through the prospect's teams. */
export interface ScholarshipPlayerTeam {
  id: string;
  name: string;
}

export interface ScholarshipPlayer {
  id: string;
  firstName: string;
  lastName: string;
  position: ScholarshipPlayerPosition | null;
  secondaryPosition: ScholarshipPlayerPosition | null;
  class: ScholarshipStoredClass | null;
  /**
   * The class the API files this row under: the row's own class, else JC1-JC3 for a JUCO player
   * with a JC year, else the player's graduation year. Optional because web and API deploy as
   * separate jobs; read it through effectiveClass, which falls back when it is missing.
   */
  effectiveClass?: string | null;
  bats: string | null;
  throws: string | null;
  cost: number | null;
  award: number | null;
  scholarshipUnit: number | null;
  nilAmount: number | null;
  nilPartner: string | null;
  revShare: number | null;
  /**
   * Academic aid in dollars. Deliberately separate from every athletic figure on this record:
   * academic money does not count against athletic equivalency, so it is never folded into
   * award, scholarshipPercent, or any board total derived from them.
   *
   * null means "not recorded", which is a different fact from a recorded 0 — the depth chart
   * shows nothing for either, but the modal must not turn an unrecorded amount into a zero.
   */
  academicAmount: number | null;
  advisor: string | null;
  residency: Residency | null;
  campus: Campus | null;
  comments: string | null;
  scholarshipPercent: number | null;
  isDsa: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;

  // ----- Prospect linkage -----
  //
  // A row attached from the prospect pool carries prospectId and takes its identity from
  // that prospect; a row typed straight onto the roster has prospectId null. The API has
  // already resolved the fields above either way, so a caller that does not care about the
  // distinction can ignore all of this and still render correctly.

  /** The linked player, or null for a row typed straight onto the roster. */
  prospectId: string | null;

  /**
   * Linked, but the player record is gone — merged away or removed. The row still renders
   * from the name snapshot taken when it was attached, because silently dropping a funded
   * roster spot would be worse than showing a stale name.
   */
  prospectMissing: boolean;

  /**
   * Graduation year, from the player record. Null on unlinked rows, which carry whatever the
   * coach set on `class` — a graduation year of their own on a recruiting board, or years of
   * eligibility on a scholarship roster. Two different facts sharing one board column.
   */
  gradYear: number | null;

  /**
   * The linked player's teams. Empty for a row typed straight onto the board, which has no
   * player record to hold a team membership.
   *
   * Optional, and ABSENT MEANS EMPTY: web and the API deploy as separate jobs, so a build that
   * reaches an older API gets no field at all. Read it as `player.teams ?? []` and never
   * distinguish "no teams" from "an API that does not send them" — there is nothing useful to
   * say about the second that is not a lie about the first.
   */
  teams?: ScholarshipPlayerTeam[];

  /**
   * High school / club. The EFFECTIVE value: the player record's on a linked row, the row's own
   * on an unlinked one.
   */
  school: string | null;

  // Everything below arrived with the roster management overhaul. The API always sends every one
  // of them (the two lists never null); they are optional in the TYPE only, to model deploy skew
  // the same way academicTotal does. Read the lists through the helpers in rosterPalette.ts.

  /** Travel team. Effective value, same rule as school. */
  travelTeam?: string | null;
  /** Height as text, e.g. 6'1". Effective value, same rule as school. */
  height?: string | null;
  /** Weight in pounds. Effective value, same rule as school. */
  weight?: number | null;

  // ----- Roster management (row-owned on both row kinds) -----

  /** Id of one of the board's evaluation traits. An id the board no longer has is ignored. */
  evaluationTraitId?: string | null;
  /** Ids of the board's indicators this player carries (at most 3). */
  indicatorIds?: string[];
  playerType?: string | null;
  playerStatus?: string | null;
  committedSchool?: string | null;
  /** Free text such as "88-91". */
  velo?: string | null;
  armAngle?: string | null;
  /** Pitch shape codes in the order the coach entered them (at most 6). */
  pitchShapes?: string[];
  pitchingRole?: string | null;

  /**
   * Recruiting stage. Read from the player record on a linked row, so every board agrees about
   * that player; read from the roster row itself on an unlinked one, which is what lets a
   * coach track a recruit they typed in by hand. Null only from an API that predates the row
   * column.
   */
  recruitingStatus: RecruitingStatus | null;

  /** The player's tags, for the chips on a board row. Always empty on an unlinked row. */
  tags: ProspectDefaultTag[];
}

/** Body for PUT /api/scholarships/{id}/players/{playerId}/status. */
export interface ScholarshipSetRecruitingStatusRequest {
  recruitingStatus: RecruitingStatus;
}

/** Body for POST /api/scholarships/{id}/players/attach. */
export interface ScholarshipAttachProspectsRequest {
  prospectIds: string[];
}

export interface ScholarshipPlayerWriteRequest {
  firstName: string;
  lastName: string;
  position?: ScholarshipPlayerPosition | null;
  secondaryPosition?: ScholarshipPlayerPosition | null;
  class?: ScholarshipStoredClass | null;
  bats?: string | null;
  throws?: string | null;
  cost?: number | null;
  award?: number | null;
  scholarshipUnit?: number | null;
  nilAmount?: number | null;
  nilPartner?: string | null;
  revShare?: number | null;
  /**
   * Academic aid in dollars; validated >= 0 server-side. The update is a full replace, so
   * OMITTING this field clears a recorded amount exactly as an explicit null does — there is
   * no "leave it alone" value. Same for every money field beside it.
   */
  academicAmount?: number | null;
  advisor?: string | null;
  residency?: Residency | null;
  campus?: Campus | null;
  comments?: string | null;
  isDsa?: boolean;
  /**
   * Roster management fields. Full replace like everything above: OMITTING one clears it. Height,
   * weight and travel team are applied to an unlinked row only; the API ignores them on a linked
   * row, so round-tripping the effective values it sent is safe.
   *
   * School is the exception: on a LINKED row the API writes it through to the player record, so it
   * ignored on a LINKED row: the player record owns it, so the board sends null and the record
   * keeps its own value. Editing a linked player's school belongs on the player record.
   */
  evaluationTraitId?: string | null;
  indicatorIds?: string[] | null;
  playerType?: string | null;
  playerStatus?: string | null;
  height?: string | null;
  weight?: number | null;
  school?: string | null;
  travelTeam?: string | null;
  committedSchool?: string | null;
  velo?: string | null;
  armAngle?: string | null;
  pitchShapes?: string[] | null;
  pitchingRole?: string | null;
}

export interface ScholarshipPlayerReorderRequest {
  position: ScholarshipPlayerPosition;
  orderedIds: string[];
}
